import React, { memo, useCallback, useMemo } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import Text from 'design-system/Text';
import { rem, hitSlop } from 'design-system/values';
import { AddToWatchListFlow } from 'utils/types/navigation';
import { CardView, TextButtonLinkSeeAll } from 'design-system';
import { PickerModal } from 'design-system/picker/PickerModal';
import IconFilterBold from 'design-system/icons/IconFilterBold';
import useColors from 'hooks/useColors';
import CreateNewItemListFooter from 'design-system/CreateNewItemListFooter';
import Amplitude from 'utils/amplitude';
import { useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { assertUnreachable } from 'utils/types';
import { InvestWalkthroughContext } from 'features/feed/components/walkthrough/context';
import ActivityItem from './ActivityItem';
import DividendActivityItem from './DividendActivityItem';
import OperationActivityItem from './OperationActivityItem';
import Position from './Position';
import EmptyState from './EmptyState';
import WatchListItem from './WatchListItem';
import CreateNewWatchListBtn from './CreateNewWatchListBtn';
import NewsCard from './NewsCard';
import { POSITION_FILTER_OPTIONS, STOCK_NEWS_SECTION_LIMIT } from '../constants';
import RecurringBuyView from './RecurringBuyView';
import DiversifyPortfolioBtn from './DiversifyPortfolioBtn';
import SwitchActivityItem from './SwitchActivityItem';
import TradingAccountCashRow from './TradingAccountCashRow';
import { selectActiveTradingAccountBroker } from '../reducers/selectors';
const strings = {
    ordersQueuedTitle: 'Queued orders',
    ordersPendingTitle: 'Pending orders',
    activityTitle: 'Activity',
    positionsTitle: 'Portfolio',
    watchlistsTitle: 'Lists',
    recurringBuysTitle: 'Recurring orders',
    newsTitle: 'News',
    viewAll: 'View All',
};
const Section = ({ assetId, showFilter, ...props }) => {
    const { navigateDeprecated } = useMainStackNavigation();
    const isInInvestPortfolioWalkthrough = React.useContext(InvestWalkthroughContext);
    const colors = useColors();
    const isAlpacaAccount = useAppSelector(selectActiveTradingAccountBroker) === 'alpaca';
    const [title, items] = useMemo(() => {
        if ('orders' in props) {
            return [props.pending ? strings.ordersPendingTitle : strings.ordersQueuedTitle, props.orders.slice(0, 3)];
        }
        if ('activities' in props) {
            return [strings.activityTitle, props.activities.slice(0, 3)];
        }
        if ('watchlists' in props) {
            return [strings.watchlistsTitle, props.watchlists];
        }
        if ('news' in props) {
            return [strings.newsTitle, props.news.slice(0, STOCK_NEWS_SECTION_LIMIT - 1)];
        }
        if ('positions' in props) {
            return [strings.positionsTitle, props.positions];
        }
        if ('recurringBuys' in props) {
            return [strings.recurringBuysTitle, props.recurringBuys];
        }
        assertUnreachable(props);
        return ['', []];
    }, [props]);
    const content = useMemo(() => {
        const itemNodes = items.map((item, index) => {
            if ('unrealizedProfitLoss' in item) {
                return <Position key={item.assetId} item={item}/>;
            }
            if ('operation' in item) {
                if (item.operation === 'switch') {
                    return <SwitchActivityItem key={item.id} item={item}/>;
                }
                return <ActivityItem key={item.id} item={item}/>;
            }
            if ('type' in item) {
                if (item.type === 'dividend') {
                    return <DividendActivityItem key={item.id} item={item}/>;
                }
                if (item.type === 'switch') {
                    return <SwitchActivityItem key={item.id} item={item}/>;
                }
                if (item.type === 'buy' || item.type === 'sell') {
                    return <ActivityItem key={item.id} item={item}/>;
                }
                if (item.type === 'withdraw' || item.type === 'deposit') {
                    return <OperationActivityItem key={item.id} item={item}/>;
                }
                return null;
            }
            if ('name' in item) {
                return <WatchListItem key={item.id} id={item.id} name={item.name} emoji={item.emoji}/>;
            }
            if ('headline' in item) {
                return <NewsCard key={item.id} data={item} noMarginRight={index === items.length - 1}/>;
            }
            if ('nonTradingDayPreference' in item) {
                return <RecurringBuyView key={item.id} recurringBuy={item}/>;
            }
            assertUnreachable(item);
            return null; // will never get here, we handled all cases
        });
        // if ('positions' in props && items.length === 0) {
        //   return <EmptyState positions />;
        // }
        if ('watchlists' in props && items.length === 0) {
            return <EmptyState watchlistPortfolio/>;
        }
        if ('recurringBuys' in props && items.length === 0) {
            return <EmptyState recurringOrders/>;
        }
        if ('news' in props) {
            // We are using ScrollView here since there will be 10 items max
            return (<ScrollView horizontal style={styles.scrollView} keyboardShouldPersistTaps="handled" showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.contentContainerStyle, props.contentContainerStyle]}>
          {itemNodes}
        </ScrollView>);
        }
        return itemNodes;
    }, [items, props]);
    const onViewAll = useCallback(() => {
        if ('orders' in props) {
            const tradingOrderType = props.pending ? 'pending' : 'queued';
            if (assetId) {
                navigateDeprecated('InvestOrders', {
                    assetId,
                    tradingOrderType,
                });
            }
            else {
                navigateDeprecated('InvestOrders', { tradingOrderType });
            }
        }
        else if ('activities' in props) {
            if (assetId) {
                navigateDeprecated('InvestActivity', { assetId });
            }
        }
        else if ('news' in props) {
            if (assetId) {
                navigateDeprecated('InvestStockNews', { assetId });
            }
        }
    }, [assetId, props]);
    const onFilterPress = useCallback(() => {
        if ('positions' in props) {
            PickerModal.show({
                pickerOptions: POSITION_FILTER_OPTIONS,
                onSelected: (_value, index) => {
                    props.setSelectedPositionFilter?.(POSITION_FILTER_OPTIONS[index]);
                },
                selectedValue: props.selectedPositionFilter,
                selectedIconFill: colors.elements.negative,
            }, {
                title: 'Sort by',
            });
        }
    }, [colors.elements.negative, props]);
    const openDiscover = useCallback(() => {
        Amplitude.logEvent('Open Discovery', {
            source: 'diversify',
        });
        navigateDeprecated('InvestDiscover');
    }, []);
    const renderTitleRight = useMemo(() => {
        if (('orders' in props && props.orders.length > 3) ||
            ('activities' in props && props.activities.length > 3) ||
            ('news' in props && props.news.length >= STOCK_NEWS_SECTION_LIMIT)) {
            return <TextButtonLinkSeeAll onPress={onViewAll}/>;
        }
        if (showFilter && 'positions' in props && !!items.length) {
            return (<TouchableOpacity onPress={onFilterPress} hitSlop={hitSlop}>
          <IconFilterBold width={rem(20)} height={rem(20)} color={colors.text.secondary}/>
        </TouchableOpacity>);
        }
        return null;
    }, [colors.text.secondary, items.length, onFilterPress, onViewAll, props, showFilter]);
    const onCreateWatchListBtnPress = useCallback(() => {
        navigateDeprecated('InvestCreateWatchList', {
            createParams: {
                flow: AddToWatchListFlow.AddToWatchListFromInvest,
            },
        });
    }, []);
    const handleAddNewRecurringOrder = useCallback(() => {
        navigateDeprecated('InvestSearchStock', { recurringOrderFlow: true });
    }, []);
    if (items.length === 0) {
        // when there are no items, we don’t render anything for Orders, Activity
        if (!('watchlists' in props || 'positions' in props || 'recurringBuys' in props) || props.hideIfEmpty) {
            return null;
        }
    }
    return (<View style={[styles.container, 'news' in props && styles.negativeMarginBottom]}>
      <View style={[
            styles.header,
            isInInvestPortfolioWalkthrough && styles.walkthroughHeaderStyles,
            'news' in props && props.contentContainerStyle,
        ]}>
        <Text Text-16 Secondary flex>
          {title}
        </Text>
        {renderTitleRight}
      </View>
      {('news' in props || !items?.length) && !('positions' in props) ? (content) : (<CardView noMargin webPadding style={[styles.cardView, isInInvestPortfolioWalkthrough && styles.walkthroughCardStyles]}>
          {'positions' in props && !isAlpacaAccount && <DiversifyPortfolioBtn onPress={openDiscover}/>}
          {'watchlists' in props && !!props.watchlists.length && (<CreateNewWatchListBtn onPress={onCreateWatchListBtnPress}/>)}
          {'recurringBuys' in props && !!props.recurringBuys.length && !props.hideIfEmpty && (<CreateNewItemListFooter title="Add a recurring order" onPress={handleAddNewRecurringOrder}/>)}
          {content}
          {'renderTotalCash' in props && props.renderTotalCash ? <TradingAccountCashRow /> : null}
        </CardView>)}
    </View>);
};
const styles = StyleSheet.create({
    container: {},
    header: {
        paddingHorizontal: rem(16),
        paddingTop: rem(24),
        paddingBottom: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    walkthroughHeaderStyles: {
        paddingTop: rem(8),
        paddingHorizontal: rem(0),
    },
    cardView: {
        marginHorizontal: rem(16),
    },
    walkthroughCardStyles: {
        marginHorizontal: rem(0),
    },
    scrollView: {
        overflow: 'visible', // not to cut shadows on iOS which extend beyond views
    },
    contentContainerStyle: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
        overflow: 'visible',
    },
    negativeMarginBottom: {
        marginBottom: -rem(8),
    },
});
const PureSection = memo(Section);
// PureSection.whyDidYouRender = true;
export default PureSection;
